import { useState } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/db7k25fidngsrf4ehm0g';

export default function FeedbackForm() {
  const [formError, setFormError] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = ((formData.get('company_alt') as string) || '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'company_alt') return;
      if (typeof value === 'string') payload.append(key, value);
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const code = parsed?.code;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (!response.ok || code !== 'OK' || isSpam) {
        setStatus('idle');
        setFormError(
          typeof serverMsg === 'string' && serverMsg ? serverMsg : '提交失败，请稍后重试。',
        );
        return;
      }

      setStatus('success');
      form.reset();
    } catch {
      setStatus('idle');
      setFormError('网络异常，提交失败，请稍后重试。');
    }
  };

  if (status === 'success') {
    return (
      <div className="flex flex-col items-center justify-center text-center py-12">
        <span className="w-16 h-16 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center mb-4">
          <i className="ri-checkbox-circle-line text-3xl"></i>
        </span>
        <h3 className="text-lg font-bold text-foreground-950">反馈已提交，感谢你的建议！</h3>
        <p className="text-sm text-foreground-500 mt-2">
          我们会认真阅读每一条留言，并据此不断改进内容与体验。
        </p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="mt-5 px-5 py-2.5 rounded-md bg-background-100 text-foreground-800 text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap"
        >
          再填一条
        </button>
      </div>
    );
  }

  return (
    <form
      id="feedback-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="space-y-4"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="fb-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
            姓名 / 昵称
          </label>
          <input
            id="fb-name"
            name="name"
            type="text"
            required
            placeholder="请输入你的称呼"
            className="w-full px-3.5 py-2.5 text-sm rounded-md bg-background-50 border border-background-300 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
          />
        </div>
        <div>
          <label htmlFor="fb-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
            邮箱
          </label>
          <input
            id="fb-email"
            name="email"
            type="email"
            required
            placeholder="you@example.com"
            className="w-full px-3.5 py-2.5 text-sm rounded-md bg-background-50 border border-background-300 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
          />
        </div>
      </div>

      <div>
        <label htmlFor="fb-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
          反馈内容
        </label>
        <textarea
          id="fb-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="告诉我们你的建议、遇到的问题或想看的内容（最多 500 字）"
          className="w-full px-3.5 py-2.5 text-sm rounded-md bg-background-50 border border-background-300 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 resize-none"
        ></textarea>
      </div>

      <div className="form-field-extra" aria-hidden="true">
        <label htmlFor="fb-company-alt">Company</label>
        <input
          id="fb-company-alt"
          name="company_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      {formError && (
        <div className="flex items-start gap-2 px-4 py-3 rounded-md bg-primary-50 border border-primary-200 text-sm text-primary-700">
          <i className="ri-error-warning-line mt-0.5"></i>
          <span>{formError}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="w-full sm:w-auto px-7 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {status === 'submitting' ? (
          <>
            <i className="ri-loader-4-line animate-spin"></i> 提交中...
          </>
        ) : (
          <>
            <i className="ri-send-plane-line"></i> 提交反馈
          </>
        )}
      </button>
      <p className="text-xs text-foreground-400">提交即表示你已阅读并同意我们用于内容改进。本表单为演示用途。</p>
    </form>
  );
}